import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatRadioModule } from '@angular/material/radio';
import { ShippingAddress } from '@api/model/shippingAddress';
import { SNAME } from '@core/constants/storage';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, combineLatest, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { LetDirective } from '@ngrx/component';
import { MenuAction } from '@core/components/menu/menu.component';
import { StickyButtonDirective } from '@core/directives/btn-sticky.directive';
import { LoadingOverlayComponent } from '@core/components/loading-overlay/loading-overlay.component';
import { AddressListComponent } from '@core/components/address-list/address-list.component';
import { PbbButtonComponent } from '@core/components/pbb-button/pbb-button.component';
import { PbbSvgIconComponent } from '@core/components';
import { Store } from '@ngrx/store';
import { selectUserProfileAddresses, selectUserProfileDefaultShippingAddressRef } from '@store/user/user.selectors';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { UserActions } from '@store/user/user.actions';
import {
  selectMerchantAddresses,
  selectMerchantName,
  selectSelectedAddressRef,
  selectIsSelectingShippingAddress,
  selectIsShippingDeliveryDetailsRequired,
  selectSelectedShippingAddress,
} from '@store/checkout/checkout.selectors';
import * as CheckoutActions from '@store/checkout/checkout.actions';
import { CheckoutSdkEventsService } from '../../services/checkout-sdk-events.service';

@Component({
  selector: 'pbb-select-shipping-address-page',
  imports: [
    CommonModule,
    MatRadioModule,
    PbbButtonComponent,
    TranslateModule,
    StickyButtonDirective,
    LetDirective,
    LoadingOverlayComponent,
    AddressListComponent,
    PbbSvgIconComponent,
    RouterLink,
  ],
  templateUrl: './select-shipping-address-page.component.html',
  styleUrls: ['./select-shipping-address-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectShippingAddressPageComponent extends UntilDestroy {
  merchantAddresses$ = this.store.select(selectMerchantAddresses);
  userAddresses$ = this.store.select(selectUserProfileAddresses);
  userDefaultAddressRef$ = this.store.select(selectUserProfileDefaultShippingAddressRef);
  selectedAddressRef$ = this.store.select(selectSelectedAddressRef);
  merchantDefaultAddressRef$: Observable<string> = of('');
  merchantName$ = this.store.select(selectMerchantName);
  isLoadingShippingAddress$ = this.store.select(selectIsSelectingShippingAddress);
  selectedShippingAddress$ = this.store.select(selectSelectedShippingAddress);
  isShippingDeliveryDetailsRequired$ = this.store.select(selectIsShippingDeliveryDetailsRequired);

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly checkoutSdkEventsService: CheckoutSdkEventsService
  ) {
    super();
  }

  onAddressSelect(address: ShippingAddress): void {
    // Only dispatch if address has both ref and postal code
    if (!address?.shipping_address_ref || !address.postal_code) {
      return;
    }
    this.store.dispatch(
      CheckoutActions.selectShippingAddressWithPostalCode({
        addressRef: address.shipping_address_ref,
        postalCode: address.postal_code,
      })
    );
  }

  onContinueClick(): void {
    combineLatest([this.selectedShippingAddress$, this.isShippingDeliveryDetailsRequired$])
      .pipe(take(1))
      .subscribe(([selectedAddress, isShippingDeliveryDetailsRequired]) => {
        if (!selectedAddress?.shipping_address_ref || !selectedAddress?.postal_code) {
          return;
        }

        if (isShippingDeliveryDetailsRequired) {
          this.checkoutSdkEventsService.requestShippingOptions(selectedAddress.postal_code);
          this.store.dispatch(
            CheckoutActions.selectShippingAddressAndFetchOptions({
              shippingAddress: selectedAddress,
            })
          );
          return;
        }

        this.store.dispatch(
          CheckoutActions.selectShippingAddressWithPostalCode({
            addressRef: selectedAddress.shipping_address_ref,
            postalCode: selectedAddress.postal_code,
          })
        );
        sessionStorage.setItem(SNAME.SET_DEVICE_VISITED, 'true');
        this.router.navigate(['/checkout/registered/orderReview']);
      });
  }

  onMenuAction(menuAction: MenuAction): void {
    switch (menuAction.action) {
      case 'default':
        this.store.dispatch(
          UserActions.setDefaultShippingAddressStart({ shippingAddressRef: menuAction?.itemRef || '' })
        );
        break;
      case 'remove':
        this.store.dispatch(UserActions.deleteShippingAddressStart({ addressIndex: menuAction?.itemIndex || 0 }));
        break;
    }
  }

  onEditAddress(event: { address: any; index: number }): void {
    this.router.navigate(['/checkout/address/edit', event.index]);
  }
}
